import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Package } from "lucide-react";
import { getInvokeErrorText } from "@/lib/invokeError";
import {
  parseLinkedAgentIdsFromConfig,
  resolveNetfaceLinkedAgentIds,
} from "@/lib/netface-integration-agents";

const FRIENDLY_ERRORS: Record<string, string> = {
  invalid_url: "URL inválida. Use o link completo começando com https://",
  host_not_allowed: "Domínio não permitido. Confira o link com a Netface.",
  hub_not_configured: "Integração temporariamente indisponível. Contate o suporte.",
  projector_not_configured: "Servidor de sincronização não configurado. Contate o suporte.",
  already_connected: "Este workspace já possui Netface conectado.",
  forbidden: "Você não tem permissão para gerenciar integrações.",
  missing_primary_agent: "Selecione o agente principal da integração.",
  invalid_primary_agent: "Agente principal inválido para este workspace.",
};

type AgentOption = { id: string; name: string; is_active: boolean };

interface NetfaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onConnectionChange?: () => void;
}

export const NetfaceDialog = ({
  open,
  onOpenChange,
  workspaceId,
  onConnectionChange,
}: NetfaceDialogProps) => {
  const [sourceUrl, setSourceUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [savingAgents, setSavingAgents] = useState(false);
  const [connected, setConnected] = useState(false);
  const [productCount, setProductCount] = useState<number | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [primaryAgentId, setPrimaryAgentId] = useState<string>("");
  const [additionalAgentIds, setAdditionalAgentIds] = useState<string[]>([]);
  const { toast } = useToast();

  const loadAgents = async () => {
    if (!workspaceId) return;
    const { data } = await supabase
      .from("agents")
      .select("id, name, is_active")
      .eq("workspace_id", workspaceId)
      .order("is_active", { ascending: false })
      .order("name");
    setAgents(data ?? []);
  };

  const loadStatus = async () => {
    if (!workspaceId) return;
    const { data } = await supabase
      .from("integrations")
      .select("id, status, config, assigned_agent_id")
      .eq("workspace_id", workspaceId)
      .eq("provider", "netface" as never)
      .in("status", ["active", "pending"])
      .maybeSingle();

    const isActive = data?.status === "active" || data?.status === "pending";
    setConnected(!!isActive);
    const config = (data?.config ?? {}) as Record<string, unknown>;
    setSourceUrl(typeof config.source_url === "string" ? config.source_url : "");
    setProductCount(typeof config.product_count === "number" ? config.product_count : null);
    setLastSyncAt(typeof config.last_sync_at === "string" ? config.last_sync_at : null);

    const primary = data?.assigned_agent_id ?? "";
    setPrimaryAgentId(primary);
    const extras = parseLinkedAgentIdsFromConfig(config).filter((id) => id !== primary);
    setAdditionalAgentIds(extras);
  };

  useEffect(() => {
    if (!open) return;
    void loadAgents();
    void loadStatus();
  }, [open, workspaceId]);

  const additionalAgentOptions = agents.filter((agent) => agent.id !== primaryAgentId);

  const toggleAdditionalAgent = (agentId: string, checked: boolean) => {
    setAdditionalAgentIds((prev) => {
      if (checked) return [...new Set([...prev, agentId])];
      return prev.filter((id) => id !== agentId);
    });
  };

  const buildLinkedAgentIds = () => resolveNetfaceLinkedAgentIds({
    assigned_agent_id: primaryAgentId || null,
    config: { linked_agent_ids: additionalAgentIds },
  });

  const saveAgents = async () => {
    if (!workspaceId || !primaryAgentId) {
      toast({
        title: "Agente principal obrigatório",
        description: "Selecione qual agente utilizará o catálogo Netface.",
        variant: "destructive",
      });
      return false;
    }

    setSavingAgents(true);
    try {
      const { data, error } = await supabase.functions.invoke("netface-update-agents", {
        body: {
          workspaceId,
          assignedAgentId: primaryAgentId,
          linkedAgentIds: buildLinkedAgentIds(),
        },
      });
      if (error) throw error;
      if (data?.error) {
        const msg = FRIENDLY_ERRORS[String(data.error)] ?? String(data.error);
        throw new Error(msg);
      }
      toast({
        title: "Agentes atualizados",
        description: "O catálogo Netface será vinculado apenas aos agentes selecionados.",
      });
      onConnectionChange?.();
      await loadStatus();
      return true;
    } catch (e) {
      toast({
        title: "Erro ao salvar agentes",
        description: getInvokeErrorText(e),
        variant: "destructive",
      });
      return false;
    } finally {
      setSavingAgents(false);
    }
  };

  const handleConnect = async () => {
    if (!workspaceId || !sourceUrl.trim()) return;
    if (!primaryAgentId) {
      toast({
        title: "Selecione o agente principal",
        description: "Escolha qual agente de IA utilizará os produtos sincronizados da Netface.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("netface-connect", {
        body: {
          workspaceId,
          sourceUrl: sourceUrl.trim(),
          assignedAgentId: primaryAgentId,
          linkedAgentIds: buildLinkedAgentIds(),
        },
      });
      if (error) throw error;
      if (data?.error) {
        const msg = FRIENDLY_ERRORS[String(data.error)] ?? String(data.error);
        throw new Error(msg);
      }
      toast({
        title: "Netface conectado!",
        description: data?.message ?? "A sincronização inicial começará em instantes.",
      });
      setConnected(true);
      onConnectionChange?.();
      await loadStatus();
    } catch (e) {
      toast({
        title: "Erro ao conectar Netface",
        description: getInvokeErrorText(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!workspaceId) return;
    setDisconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("netface-disconnect", {
        body: { workspaceId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      toast({
        title: "Netface desconectado",
        description: "Os produtos permanecem no catálogo; a sincronização foi pausada.",
      });
      setConnected(false);
      onConnectionChange?.();
      await loadStatus();
    } catch (e) {
      toast({
        title: "Erro ao desconectar",
        description: getInvokeErrorText(e),
        variant: "destructive",
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const agentSelectionBlock = (
    <div className="space-y-3 rounded-md border p-3">
      <div className="space-y-1.5">
        <Label>Agente principal</Label>
        <Select value={primaryAgentId || "none"} onValueChange={(v) => setPrimaryAgentId(v === "none" ? "" : v)}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione o agente principal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Selecione um agente</SelectItem>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.is_active ? agent.name : `${agent.name} (Inativo)`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          O agente principal recebe automaticamente os produtos ativos sincronizados da Netface.
        </p>
      </div>

      {additionalAgentOptions.length > 0 && (
        <div className="space-y-2">
          <Label>Outros agentes com acesso ao catálogo</Label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {additionalAgentOptions.map((agent) => (
              <label key={agent.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={additionalAgentIds.includes(agent.id)}
                  onCheckedChange={(checked) => toggleAdditionalAgent(agent.id, checked === true)}
                />
                <span>{agent.is_active ? agent.name : `${agent.name} (Inativo)`}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Netface
          </DialogTitle>
          <DialogDescription>
            Conecte a Netface e defina quais agentes de IA utilizam o catálogo sincronizado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="netface-url">Link da Netface</Label>
            <Input
              id="netface-url"
              placeholder="https://sualoja.com.br/..."
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              disabled={connected || loading}
            />
            <p className="text-xs text-muted-foreground">
              Cole o link que a Netface enviou para você. Produtos ativos serão vinculados
              somente aos agentes configurados abaixo.
            </p>
          </div>

          {agentSelectionBlock}

          {connected && (
            <div className="rounded-md border p-3 text-sm space-y-1">
              <p><span className="font-medium">Status:</span> Conectado</p>
              {productCount != null && (
                <p><span className="font-medium">Produtos ativos:</span> {productCount}</p>
              )}
              {lastSyncAt && (
                <p><span className="font-medium">Última sincronização:</span> {new Date(lastSyncAt).toLocaleString("pt-BR")}</p>
              )}
              {primaryAgentId && (
                <p>
                  <span className="font-medium">Agentes com catálogo:</span>{" "}
                  {buildLinkedAgentIds().length}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
          {connected ? (
            <>
              <Button
                variant="outline"
                onClick={() => void saveAgents()}
                disabled={savingAgents || !primaryAgentId}
              >
                {savingAgents ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salvar agentes
              </Button>
              <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
                {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Desconectar"}
              </Button>
            </>
          ) : (
            <Button onClick={handleConnect} disabled={loading || !sourceUrl.trim() || !primaryAgentId}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Conectar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
